import instance, { isAxiosErrorHandled } from 'api';
import Button from 'components/button/Button';
import FormTextField from 'components/form/FormTextField';
import { TFAField } from 'components/form/TFAField';
import { Modal } from 'components/modal/Modal';
import { endpoints } from 'endpoints.config';
import { getErrorMessage } from 'errors';
import { Form, Formik, FormikHelpers } from 'formik';
import { toCamelCase } from 'helpers/formatFormFieldNames';
import { Toast, ToastMessageReason } from 'helpers/toast';
import { usePost } from 'helpers/usePost';
import { useTFAField } from 'helpers/useTFAField';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { closeModal } from 'reducers/modal';
import * as Yup from 'yup';

const initialValues = {
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    tfaCode: '',
    useSms: false,
};

const getValidationSchema = (t: (key: string, options?: any) => any) =>
    Yup.object({
        showTfa: Yup.boolean(),
        oldPassword: Yup.string().required(t('enterCurrentPassword')),
        newPassword: Yup.string()
            .required(t('enterNewPassword'))
            .min(8, t('passwordMinLength'))
            .matches(/[0-9]/, t('passwordMustContainNumber'))
            .matches(/[a-zA-Z]/, t('passwordMustContainLetter')),
        confirmNewPassword: Yup.string()
            .required(t('confirmNewPasswordRequired'))
            .test('passwords-match', t('passwordsMustMatch'), (value: any, testContext?: any) => {
                return testContext.parent.newPassword === value;
            }),
        tfaCode: Yup.string().when('showTfa', {
            is: true,
            then: Yup.string().required(t('enterTfaCode')),
        }),
    });

export const ChangePasswordModal = () => {
    const { t } = useTranslation('security');
    const dispatch = useDispatch();
    const { post, errorMessage } = usePost();
    const [isFormError, setIsFormError] = useState(false);

    const [showTfa, setShowTfa] = useState(false);
    const [TFAType, toggleTFAType] = useTFAField(showTfa);

    const initialValues = {
        showTfa: false,
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: '',
        tfaCode: '',
    };

    const createModalButtons = (submitfn: () => void, submitting: boolean) => (
        <div className="ModalNavigation">
            <Button onClick={() => dispatch(closeModal())} priority="secondary" type={'button'} className="notranslate">
                {t('close', { ns: 'common' })}
            </Button>
            <Button onClick={submitfn} priority="primary" disabled={submitting} type={'submit'} className="notranslate">
                {t('changePassword')}
            </Button>
        </div>
    );
    const handleSubmit = (
        values: typeof initialValues,
        formikHelpers: FormikHelpers<typeof initialValues>
    ) => {
        if (!values.showTfa) {
            formikHelpers.setFieldValue('showTfa', true);
            setShowTfa(true);
            formikHelpers.setSubmitting(false);
            formikHelpers.setFieldTouched('tfaCode', false);
            return;
        }
        post(endpoints.profilemodule.updatePassword, { ...values, tfaType: TFAType })
            .then(() => {
                Toast.openToastMessage(t('passwordChanged'), ToastMessageReason.VALID);
                dispatch(closeModal());
            })
            .catch((err) => {
                if (isAxiosErrorHandled(err) && err.response.data.errors) {
                    err.response.data.errors.forEach((error) => {
                        const fieldName = toCamelCase(error.fieldName) as string;
                        if (Object.keys(getValidationSchema(t).fields).includes(fieldName)) {
                            setIsFormError(true);
                            formikHelpers.setFieldError(
                                fieldName,
                                getErrorMessage(error.messageCode)
                            );
                        }
                    });
                }
            })
            .finally(() => {
                formikHelpers.setSubmitting(false);
            });
    };
    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={getValidationSchema(t)}
        >
            {({ submitForm, isSubmitting, values, setFieldValue, setFieldTouched }) => (
                <Form>
                    <Modal
                        title={t('changePasswordTitle')}
                        customButtons={createModalButtons(submitForm, isSubmitting)}
                    >
                        <FormTextField
                            field="oldPassword"
                            label={t('oldPassword')}
                            type="password"
                            required
                            onBlur={(e) => {
                                setFieldValue('oldPassword', e.target.value.trim(), true);
                                setFieldTouched('oldPassword', true);
                            }}
                        />
                        <FormTextField
                            field="newPassword"
                            label={t('newPassword')}
                            type="password"
                            hint={t('passwordHint')}
                            required
                            onBlur={(e) => {
                                setFieldValue('newPassword', e.target.value.trim(), true);
                                setFieldTouched('newPassword', true);
                            }}
                        />
                        <FormTextField
                            field="confirmNewPassword"
                            label={t('confirmNewPassword')}
                            type="password"
                            required
                            onBlur={(e) => {
                                setFieldValue('confirmNewPassword', e.target.value.trim(), true);
                                setFieldTouched('confirmNewPassword', true);
                            }}
                        />
                        {values.showTfa && (
                            <TFAField
                                field={'tfaCode'}
                                label={t('tfaCode')}
                                required={true}
                                toggleTFAType={toggleTFAType}
                                tfaType={TFAType}
                                autoFocus
                            />
                        )}
                        {errorMessage && !isFormError && (
                            <div className="ErrorText">{errorMessage}</div>
                        )}
                    </Modal>
                </Form>
            )}
        </Formik>
    );
};
